import type { Tracker } from '../interfaces/tracker';
import type { Types } from '@amplitude/analytics-browser';
import * as amplitude from '@amplitude/analytics-browser';
import { invariant } from '../../utils/commonUtils';
import { TrackerEvent } from '../interfaces/tracker-event';

const map = new Map([
  ['ALTIBOX_NORWAY_-1', 'amplitude_guest'],
  ['ALTIBOX_NORWAY_5', 'amplitude_bugfix'],
  ['ALTIBOX_NORWAY_100', 'amplitude_internal'],
  ['ALTIBOX_NORWAY_4701', 'amplitude_norway'],
  ['ALTIBOX_NORWAY_9999', 'amplitude_pilot'],
  ['ALTIBOX_NORWAY_2222', 'amplitude_pilot'],
  ['ALTIBOX_DENMARK_-1', 'amplitude_guest'],
  ['ALTIBOX_DENMARK_10', 'amplitude_bugfix'],
  ['ALTIBOX_DENMARK_200', 'amplitude_internal'],
  ['ALTIBOX_DENMARK_300', 'amplitude_denmark'],
  ['ALTIBOX_DENMARK_4501', 'amplitude_denmark'],
  ['ALTIBOX_DENMARK_430', 'amplitude_pilot'],
  ['ALTIBOX_DENMARK_3333', 'amplitude_pilot'],
]);

type Config = {
  key: string;
  value: string;
}[];

export class AmplitudeTracker implements Tracker {
  private readonly identity = new amplitude.Identify();

  private userGroup: string | undefined;
  private tenant: string | undefined;
  private partnerId: number | undefined;

  constructor(private readonly options?: Types.BrowserOptions) {}

  public async init(config: Config, bossId: string, userGroup: string) {
    const parameter = map.get(`${bossId}_${userGroup}`);

    if (parameter === undefined) {
      throw new Error('Parameter not found');
    }

    const key = config.find((x) => x.key === parameter)?.value;

    if (key === undefined) {
      throw new Error('Key not found');
    }

    this.userGroup = userGroup;
    this.identity.set('usergroup_id', this.userGroup);

    amplitude.init(key, this.options);
    amplitude.identify(this.identity);

    return key;
  }

  public async identifyUser(subscriberId: string, bossId: string, partnerId: number) {
    const tenant = bossId.replace('ALTIBOX_', '');
    const key = `${subscriberId}${tenant}${partnerId}`;

    const encoder = new TextEncoder();
    const data = encoder.encode(key);
    const hash = await crypto.subtle.digest('SHA-256', data);
    const userId = Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    this.tenant = tenant;
    this.partnerId = partnerId;

    this.identity.set('altibox_tenant', tenant);
    this.identity.set('altibox_partner', partnerId);

    invariant(this.userGroup !== undefined, 'User group is not set');

    this.identity.set('usergroup_id', this.userGroup);

    amplitude.setUserId(userId);
    amplitude.identify(this.identity);

    return {
      altibox_tenant: tenant,
      altibox_partner: partnerId,
      usergroup_id: this.userGroup,
      user_id: userId,
    };
  }

  public enable() {
    amplitude.setOptOut(false);
  }

  public disable() {
    amplitude.setOptOut(true);
  }

  public reset() {
    amplitude.reset();

    if (this.tenant) {
      this.identity.remove('altibox_tenant', this.tenant);
    }

    if (this.partnerId) {
      this.identity.remove('altibox_partner', this.partnerId);
    }
  }

  public track(event: TrackerEvent<object>) {
    amplitude.track(event.name, event.properties);
  }
}
